import { useState } from 'react';

import clsxm from '@/lib/clsxm';

import { tone } from '@/components/agents/primitives';

import { imageSrcSet, imageUrl } from './assets';
import CompareView from './CompareView';
import { useInteriorLandingT, useRoomVars } from './kit';
import type { InteriorLandingPage } from './types';

const HERO_SIZES = '(min-width: 1280px) 600px, (min-width: 1024px) 46vw, 94vw';

/**
 * The hero's proof: a photo and its redesigns (drag to compare), or finished
 * designs on pages about spaces we have no before photos of. Pages with
 * several scenes get tabs above it; rooms with several looks get chips below.
 */
export default function Showcase({ page }: { page: InteriorLandingPage }) {
  const { t } = useInteriorLandingT();
  const roomVars = useRoomVars();
  const [sceneIndex, setSceneIndex] = useState(0);
  const [looks, setLooks] = useState<Record<number, number>>({});

  const scene = page.scenes[sceneIndex];
  const lookIndex = looks[sceneIndex] ?? scene.initialLook ?? 0;
  const look = scene.looks[lookIndex] ?? scene.looks[0];
  const frame = scene.before ?? look.image;
  const sceneVars = roomVars(scene.label);

  return (
    <figure className='relative'>
      {page.scenes.length > 1 ? (
        <div
          role='tablist'
          aria-label={t('Examples')}
          className='-mx-1 mb-3 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
        >
          {page.scenes.map((item, index) => (
            <button
              key={item.label}
              type='button'
              role='tab'
              aria-selected={index === sceneIndex}
              onClick={() => setSceneIndex(index)}
              className={clsxm(
                'flex-none rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors duration-200',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
                index === sceneIndex
                  ? 'bg-[#10101c] text-white dark:bg-white dark:text-[#10101c]'
                  : clsxm(
                      'bg-white ring-1 ring-[#0f0f28]/[0.08] hover:ring-[#0f0f28]/20 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:ring-white/25',
                      tone.body,
                    ),
              )}
            >
              {t(item.label)}
            </button>
          ))}
        </div>
      ) : null}

      <div
        className='relative overflow-hidden rounded-[22px] bg-[#e9e9f2] shadow-[0_40px_80px_-40px_rgba(22,12,90,0.55),0_1px_2px_rgba(15,15,40,0.06)] ring-1 ring-[#0f0f28]/[0.08] sm:rounded-[28px] dark:bg-[#1b1b28] dark:ring-white/10'
        style={{ aspectRatio: `${frame.aspect}` }}
      >
        {scene.before ? (
          <CompareView
            before={scene.before}
            after={look.image}
            beforeAlt={t('{{room}}: the original photo', sceneVars)}
            afterAlt={t('{{room}} redesigned with AI: {{look}}', {
              ...sceneVars,
              look: t(look.label),
            })}
            labels={{ before: t('Before'), after: t('After') }}
            sliderLabel={t('Compare before and after')}
            sizes={HERO_SIZES}
            initial={scene.split}
            eager={sceneIndex === 0}
          />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={look.image.path}
            src={imageUrl(look.image, 960)}
            srcSet={imageSrcSet(look.image)}
            sizes={HERO_SIZES}
            alt={t('{{room}} design idea made with AI', sceneVars)}
            className='absolute inset-0 h-full w-full object-cover'
            fetchPriority={sceneIndex === 0 ? 'high' : undefined}
            loading={sceneIndex === 0 ? 'eager' : 'lazy'}
            decoding='async'
          />
        )}
      </div>

      {scene.looks.length > 1 ? (
        <div className='mt-4 flex flex-wrap items-center gap-2'>
          <span className={clsxm('me-1 text-[13px] font-medium', tone.quiet)}>
            {t('Look')}
          </span>
          {scene.looks.map((item, index) => (
            <button
              key={item.label}
              type='button'
              aria-pressed={index === lookIndex}
              onClick={() =>
                setLooks((current) => ({ ...current, [sceneIndex]: index }))
              }
              className={clsxm(
                'flex items-center gap-2 rounded-full py-1 pe-3.5 ps-1 text-[13px] font-semibold transition-all duration-200',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
                index === lookIndex
                  ? 'bg-white text-[#10101c] shadow-[0_6px_16px_-8px_rgba(22,12,90,0.5)] ring-2 ring-primary-500 dark:bg-white/[0.08] dark:text-white'
                  : clsxm(
                      'bg-white/70 ring-1 ring-[#0f0f28]/[0.08] hover:bg-white dark:bg-white/[0.04] dark:ring-white/10',
                      tone.body,
                    ),
              )}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imageUrl(item.image, 320)}
                alt=''
                width={28}
                height={28}
                loading='lazy'
                decoding='async'
                className='h-7 w-7 rounded-full object-cover'
              />
              {t(item.label)}
            </button>
          ))}
        </div>
      ) : (
        <figcaption
          className={clsxm(
            'mt-4 flex items-center gap-2 text-[13px] font-medium',
            tone.quiet,
          )}
        >
          {look.swatch ? (
            <span
              aria-hidden='true'
              className='h-4 w-4 flex-none rounded-full ring-1 ring-[#0f0f28]/15'
              style={{ backgroundColor: look.swatch }}
            />
          ) : null}
          {scene.before ? (
            <>
              <span className={tone.ink}>{t(look.label)}</span>
              <span aria-hidden='true'>·</span>
              {t('Drag to compare')}
            </>
          ) : (
            t('A design idea made with AI')
          )}
        </figcaption>
      )}
    </figure>
  );
}
